import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  UserIcon,
  BuildingOfficeIcon,
  FolderIcon,
  TicketIcon,
} from '@heroicons/react/24/outline'
import {
  useListMoConversationsQuery,
  useGetMoConversationQuery,
  useGetMoUserProjectStreamQuery,
  MoConversationRow,
  MoMessageRow,
  ConversationSource,
} from '../store/api/motionodeAiApi'
import { formatDateTimeEST } from '../utils/dateUtils'

const MESSAGE_PAGE_SIZE = 100

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}

function SourceBadge({ source }: { source: ConversationSource | string | undefined }) {
  const isBuilder = source === 'ai_builder'
  const isAgent = source === 'agent'
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
        isBuilder
          ? 'bg-violet-50 text-violet-700 border border-violet-200'
          : isAgent
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-sky-50 text-sky-700 border border-sky-200'
      }`}
    >
      {isBuilder ? 'AI Builder' : isAgent ? 'Agent' : 'Mo'}
    </span>
  )
}

function parseMoMetadata(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null
  if (typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
    } catch {
      return null
    }
  }
  return null
}

function messageKey(message: MoMessageRow): string {
  return String(message.uuid || message.id)
}

/** Mo→agent handoffs are stored as role=user with askedBy=mo — never label those "User". */
function isMoAuthoredMessage(message: MoMessageRow): boolean {
  if (message.is_mo_bot) return true
  const askedBy = String(parseMoMetadata(message.metadata_json)?.askedBy || '').toLowerCase()
  return askedBy === 'mo'
}

function isHumanUserMessage(message: MoMessageRow): boolean {
  return message.role === 'user' && !isMoAuthoredMessage(message)
}

function previewText(content: string | null | undefined, max = 30): string {
  const compact = String(content || '').replace(/\s+/g, ' ').trim()
  if (!compact) return '(empty)'
  return compact.length > max ? `${compact.slice(0, max)}…` : compact
}

function formatJson(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function MessageBubble({
  message,
  source,
  id,
}: {
  message: MoMessageRow
  source: ConversationSource
  id?: string
}) {
  const isBuilder = source === 'ai_builder'
  const isTool = message.role === 'tool'
  const isSystem = message.role === 'system'
  const isMoBot = isMoAuthoredMessage(message)
  const isHumanUser = isHumanUserMessage(message)
  const meta = parseMoMetadata(message.metadata_json)
  const thinkingTrace = Array.isArray(meta?.thinkingTrace) ? meta.thinkingTrace : null
  const ops = meta?.ops && typeof meta.ops === 'object' ? (meta.ops as Record<string, unknown>) : null
  const showMoDebug = Boolean(thinkingTrace?.length || ops)
  const showBuilderDebug = isBuilder && (isTool || Boolean(message.tool_result_summary) || Boolean(message.tool_args))

  const alignEnd = (isHumanUser || isMoBot) && !isTool && !isSystem
  const bubbleClass = isTool || isSystem
    ? 'bg-white text-gray-800 border border-gray-200 max-w-full'
    : isMoBot
      ? 'bg-violet-700 text-white'
      : isHumanUser
        ? 'bg-indigo-600 text-white'
        : 'bg-gray-100 text-gray-900 border border-gray-200'

  const speaker = (() => {
    if (isMoBot) return 'Mo'
    if (message.speaker_label && !/^user$/i.test(message.speaker_label)) return message.speaker_label
    if (isTool) return `Tool · ${message.tool_name || message.kind || 'tool'}`
    if (isSystem) return 'System'
    if (isHumanUser) return 'User'
    if (isBuilder) return 'AI Builder'
    if (source === 'agent') return 'Agent'
    return 'Mo'
  })()

  return (
    <div id={id} className={`flex ${alignEnd ? 'justify-end' : 'justify-start'} scroll-mt-3`}>
      <div
        className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          isTool || isSystem ? 'w-full' : 'max-w-[85%]'
        } ${bubbleClass}`}
      >
        <div className="flex items-center gap-2 mb-1 opacity-70 text-[10px] uppercase tracking-wide">
          <span className={isMoBot ? 'normal-case tracking-normal font-semibold opacity-100' : ''}>
            {speaker}
          </span>
          {message.kind && message.kind !== 'text' && message.kind !== message.role ? (
            <span>· {message.kind}</span>
          ) : null}
          {showMoDebug || (isBuilder && isTool) ? (
            <span className={isHumanUser || isMoBot ? 'text-indigo-100' : 'text-amber-700'}>· debug</span>
          ) : null}
          <span>· {formatDateTimeEST(message.created_at)}</span>
        </div>

        {message.content ? (
          <div>{message.content}</div>
        ) : !showBuilderDebug ? (
          <span className="italic opacity-60">(empty)</span>
        ) : null}

        {showMoDebug ? (
          <div className={`mt-2 rounded border px-2 py-1.5 text-[11px] font-mono leading-relaxed ${
            isHumanUser || isMoBot ? 'border-indigo-400/40 bg-indigo-700/40' : 'border-gray-300 bg-white text-gray-800'
          }`}>
            <div className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-wide opacity-70">
              Thinking details
            </div>
            {thinkingTrace?.map((step, i) => {
              const row = step && typeof step === 'object' ? (step as Record<string, unknown>) : {}
              const label = String(row.label || row.kind || `Step ${i + 1}`)
              const bits = [
                row.serviceName ? String(row.serviceName) : null,
                row.envKind ? String(row.envKind) : null,
                row.rowCount != null ? `${row.rowCount} rows` : null,
                row.via ? `via ${row.via}` : null,
              ].filter(Boolean)
              return (
                <div key={`dbg-${message.uuid}-${i}`} className="mb-2 last:mb-0">
                  <div>{label}{bits.length ? ` · ${bits.join(' · ')}` : ''}</div>
                  {row.sql ? (
                    <pre className="mt-1 whitespace-pre-wrap break-words opacity-90">{String(row.sql)}</pre>
                  ) : null}
                  {row.error ? (
                    <div className="mt-1 text-red-700">{String(row.error)}</div>
                  ) : null}
                  {row.detail ? (
                    <div className="mt-1 opacity-80">{String(row.detail)}</div>
                  ) : null}
                </div>
              )
            })}
            {ops && !thinkingTrace?.length ? (
              <pre className="whitespace-pre-wrap break-words opacity-90">
                {JSON.stringify(ops, null, 2)}
              </pre>
            ) : null}
          </div>
        ) : null}

        {showBuilderDebug ? (
          <div className="mt-2 rounded border border-violet-200 bg-violet-50/80 px-2 py-1.5 text-[11px] font-mono leading-relaxed text-gray-800">
            <div className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              Tool details (admin debug)
            </div>
            {message.tool_name ? (
              <div className="mb-1">
                <span className="opacity-60">name:</span> {message.tool_name}
              </div>
            ) : null}
            {message.approval_status && message.approval_status !== 'none' ? (
              <div className="mb-1">
                <span className="opacity-60">approval:</span> {message.approval_status}
                {message.approval_reason ? ` — ${message.approval_reason}` : ''}
              </div>
            ) : null}
            {message.tool_args != null ? (
              <div className="mb-2">
                <div className="opacity-60 mb-0.5">args</div>
                <pre className="whitespace-pre-wrap break-words max-h-64 overflow-auto">
                  {formatJson(message.tool_args)}
                </pre>
              </div>
            ) : null}
            {message.tool_result_summary ? (
              <div>
                <div className="opacity-60 mb-0.5">result</div>
                <pre className="whitespace-pre-wrap break-words max-h-80 overflow-auto">
                  {message.tool_result_summary}
                </pre>
              </div>
            ) : null}
            {(message.tokens_in != null || message.tokens_out != null) ? (
              <div className="mt-1 opacity-60">
                tokens in/out: {message.tokens_in ?? '—'} / {message.tokens_out ?? '—'}
                {message.cost_estimate != null ? ` · est $${message.cost_estimate}` : ''}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function ConversationTranscript({
  messages,
  source,
  resetKey,
}: {
  messages: MoMessageRow[]
  source: ConversationSource
  resetKey: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(MESSAGE_PAGE_SIZE)
  const [activeIndexId, setActiveIndexId] = useState<string | null>(null)

  const newestFirst = useMemo(() => {
    return [...messages].sort((a, b) => {
      const tb = new Date(b.created_at || 0).getTime()
      const ta = new Date(a.created_at || 0).getTime()
      if (tb !== ta) return tb - ta
      return Number(b.id || 0) - Number(a.id || 0)
    })
  }, [messages])

  const userIndex = useMemo(() => {
    return newestFirst
      .filter((m) => isHumanUserMessage(m))
      .map((m) => ({
        id: messageKey(m),
        preview: previewText(m.content, 30),
        createdAt: m.created_at,
      }))
  }, [newestFirst])

  useEffect(() => {
    setVisibleCount(MESSAGE_PAGE_SIZE)
    setActiveIndexId(null)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [resetKey, newestFirst.length])

  const visibleMessages = newestFirst.slice(0, visibleCount)
  const hasMore = visibleCount < newestFirst.length

  const ensureVisibleThenScroll = (targetId: string) => {
    const index = newestFirst.findIndex((m) => messageKey(m) === targetId)
    if (index < 0) return
    const needed = Math.max(MESSAGE_PAGE_SIZE, index + 1)
    if (needed > visibleCount) setVisibleCount(needed)
    setActiveIndexId(targetId)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(`msg-${targetId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    })
  }

  const onScroll = () => {
    const el = scrollRef.current
    if (!el || !hasMore) return
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 160
    if (nearBottom) {
      setVisibleCount((n) => Math.min(newestFirst.length, n + MESSAGE_PAGE_SIZE))
    }
  }

  if (newestFirst.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-12">No messages in this view.</p>
    )
  }

  return (
    <div className="flex-1 min-h-0 flex">
      <aside className="w-56 shrink-0 border-r border-gray-200 bg-white flex flex-col">
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            User messages
          </p>
          <p className="text-[11px] text-gray-400">{userIndex.length} turns</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {userIndex.length === 0 ? (
            <p className="px-3 py-4 text-xs text-gray-400">
              No human user messages in this thread.
            </p>
          ) : (
            userIndex.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => ensureVisibleThenScroll(item.id)}
                className={`w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-indigo-50 transition-colors ${
                  activeIndexId === item.id ? 'bg-indigo-50' : ''
                }`}
              >
                <div className="text-xs font-medium text-gray-800 truncate">{item.preview}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  {formatDateTimeEST(item.createdAt)}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50"
      >
        {visibleMessages.map((m) => (
          <MessageBubble
            key={messageKey(m)}
            id={`msg-${messageKey(m)}`}
            message={m}
            source={source}
          />
        ))}
        {hasMore ? (
          <div className="flex justify-center py-3">
            <button
              type="button"
              onClick={() => setVisibleCount((n) => Math.min(newestFirst.length, n + MESSAGE_PAGE_SIZE))}
              className="px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50"
            >
              Load older ({Math.min(MESSAGE_PAGE_SIZE, newestFirst.length - visibleCount)} more) ·{' '}
              {visibleCount}/{newestFirst.length}
            </button>
          </div>
        ) : (
          <p className="text-center text-[11px] text-gray-400 py-2">
            End of thread · {newestFirst.length} messages
          </p>
        )}
      </div>
    </div>
  )
}

function ConversationDrawer({
  uuid,
  onClose,
}: {
  uuid: string
  onClose: () => void
}) {
  const [viewMode, setViewMode] = useState<'thread' | 'full' | 'agents'>('thread')
  const { data, isLoading, isFetching, refetch } = useGetMoConversationQuery(uuid)
  const detail = data?.data
  const thread = detail?.thread
  const source = detail?.source || thread?.source || 'mo'
  const isBuilder = source === 'ai_builder'
  const isAgent = source === 'agent'
  const isMo = !isBuilder && !isAgent
  const threadMessages = detail?.messages ?? []
  const collaborator = detail?.collaborator

  const streamArgs = useMemo(
    () =>
      thread && isMo && thread.user_id
        ? { userId: thread.user_id, projectId: thread.project_id, limit: 5000 }
        : { userId: 0, projectId: 0 },
    [thread, isMo],
  )
  const {
    data: streamData,
    isLoading: streamLoading,
    isFetching: streamFetching,
  } = useGetMoUserProjectStreamQuery(streamArgs, {
    skip: viewMode !== 'full' || !thread || !isMo || !thread.user_id,
  })

  const messages =
    viewMode === 'full' && isMo ? (streamData?.data?.messages ?? []) : threadMessages
  const loading = viewMode === 'full' && isMo
    ? streamLoading || streamFetching
    : isLoading || isFetching
  const truncatedHint = (() => {
    if (viewMode === 'full' && isMo && streamData?.data?.truncated) {
      return `Showing latest ${messages.length.toLocaleString()} of ${Number(streamData.data.total_count || 0).toLocaleString()} messages`
    }
    if (viewMode === 'thread' && isMo && detail?.truncated && detail?.total_message_count) {
      return `Showing latest ${messages.length.toLocaleString()} of ${Number(detail.total_message_count).toLocaleString()} messages`
    }
    return null
  })()

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-5xl bg-white shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <SourceBadge source={source} />
              <span className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
                Full debug
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {thread?.title || 'Conversation'}
            </h2>
            {thread?.short_id ? (
              <p className="mt-0.5 text-xs font-mono text-indigo-700">
                Topic ID {String(thread.short_id).padStart(8, '0')}
              </p>
            ) : null}
            {thread && (
              <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                <p className="flex items-center gap-1.5 min-w-0">
                  <UserIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {isBuilder || isAgent
                      ? `User: ${collaborator?.name || thread.user_name || '—'} · ${collaborator?.email || thread.user_email || '—'}`
                      : `${thread.user_name || '—'} · ${thread.user_email || '—'}`}
                  </span>
                </p>
                <p className="flex items-center gap-1.5 min-w-0">
                  <FolderIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{thread.project_name || '—'}</span>
                </p>
                <p className="flex items-center gap-1.5 min-w-0">
                  <BuildingOfficeIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{thread.organization_name || '—'}</span>
                </p>
                {(isBuilder || isAgent) && (thread.ticket_uuid || thread.scope_label) ? (
                  <p className="flex items-center gap-1.5 min-w-0">
                    <TicketIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {thread.scope_label || thread.ticket_uuid}
                      {thread.ticket_title ? ` · ${thread.ticket_title}` : ''}
                    </span>
                  </p>
                ) : null}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100"
              title="Refresh"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isMo ? (
          <div className="px-6 py-2 border-b border-gray-100 flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('thread')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                viewMode === 'thread'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              This topic
            </button>
            <button
              type="button"
              onClick={() => setViewMode('full')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                viewMode === 'full'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Full user stream in project
            </button>
            <button
              type="button"
              onClick={() => setViewMode('agents')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                viewMode === 'agents'
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Linked agents
              {detail?.agentSessions?.length
                ? ` (${detail.agentSessions.length})`
                : ''}
            </button>
          </div>
        ) : (
          <div className="px-6 py-2 border-b border-gray-100 text-xs text-gray-500 shrink-0">
            {isAgent
              ? 'Newest first. Mo handoffs are labeled Mo (not User). Left index lists human user turns.'
              : (
                <>
                  Showing every AI Builder chat message for this ticket (tools, args, results included).
                  Mo-authored turns are labeled <span className="font-medium text-violet-700">Mo</span>.
                </>
              )}
          </div>
        )}

        {truncatedHint ? (
          <div className="px-6 py-2 border-b border-amber-100 bg-amber-50 text-xs text-amber-800 shrink-0">
            {truncatedHint}
          </div>
        ) : null}

        <div className="flex-1 min-h-0 flex flex-col bg-gray-50">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : viewMode === 'agents' && isMo ? (
            !(detail?.agentSessions?.length) ? (
              <p className="text-sm text-gray-500 text-center py-12">
                No agent sessions for this user in this project.
              </p>
            ) : (
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
                {detail.agentSessions.map((session) => (
                  <div key={session.uuid} className="space-y-2">
                    <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur px-2 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-md">
                      {session.collaborator_key}
                      <span className="ml-2 font-normal text-gray-500">
                        {session.status} · {session.uuid.slice(0, 8)}
                      </span>
                    </div>
                    <div className="h-[60vh] border border-gray-200 rounded-md overflow-hidden bg-white flex flex-col">
                      <ConversationTranscript
                        messages={session.messages || []}
                        source="agent"
                        resetKey={`${session.uuid}-${(session.messages || []).length}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <ConversationTranscript
              messages={messages}
              source={source}
              resetKey={`${uuid}-${viewMode}-${messages.length}`}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default function MotionodeAi() {
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [source, setSource] = useState<ConversationSource | 'all'>('all')
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)

  const { data, isLoading, isFetching, isError, error, refetch } = useListMoConversationsQuery({
    page,
    limit: 50,
    search,
    source,
  })

  const rows = data?.data ?? []
  const stats = data?.stats
  const meta = data?.meta
  const listErrorMessage = (() => {
    if (!isError) return null
    const err = error as { data?: { message?: string }; status?: number | string; error?: string }
    return err?.data?.message
      || (typeof err?.error === 'string' ? err.error : null)
      || (err?.status ? `Request failed (${err.status})` : null)
      || 'Failed to load Motionode AI conversations'
  })()

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-7 w-7 text-indigo-600" />
            Motionode AI
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Mo chats, agent collaborator sessions, and AI Builder ticket conversations —
            full debug for admins. Includes members, clients, stakeholders, and guests.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {listErrorMessage ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {listErrorMessage}
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard label="All topics" value={String(stats?.total_threads ?? (isError ? '—' : '0'))} />
        <StatCard label="Mo" value={String(stats?.mo_threads ?? (isError ? '—' : '0'))} />
        <StatCard label="Agents" value={String(stats?.agent_threads ?? (isError ? '—' : '0'))} />
        <StatCard label="AI Builder" value={String(stats?.builder_threads ?? (isError ? '—' : '0'))} />
        <StatCard label="Messages" value={String(stats?.total_messages ?? (isError ? '—' : '0'))} />
        <StatCard label="Users" value={String(stats?.unique_users ?? (isError ? '—' : '0'))} />
        <StatCard label="Projects" value={String(stats?.unique_projects ?? (isError ? '—' : '0'))} />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <form onSubmit={applySearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by topic ID (8 digits), user, email, project, organization, topic, or ticket…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Search
          </button>
          {search ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput('')
                setSearch('')
                setPage(1)
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          ) : null}
        </form>
        <div className="flex flex-wrap gap-2">
          {([
            ['all', 'All'],
            ['mo', 'Mo'],
            ['agent', 'Agents'],
            ['ai_builder', 'AI Builder'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setSource(value)
                setPage(1)
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                source === value
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Topic / Ticket
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Msgs
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Last activity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                      Loading conversations…
                    </div>
                  </td>
                </tr>
              ) : listErrorMessage ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-red-600">
                    Could not load conversations. Check admin API logs / database connection.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                    No conversations found.
                  </td>
                </tr>
              ) : (
                rows.map((row: MoConversationRow) => (
                  <tr
                    key={`${row.source}-${row.uuid}`}
                    onClick={() => setSelectedUuid(row.uuid)}
                    className="hover:bg-indigo-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm">
                      <SourceBadge source={row.source} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{row.user_name || '—'}</div>
                      <div className="text-xs text-gray-500">{row.user_email || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.project_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.organization_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 truncate max-w-[280px]">
                        {row.title || 'Untitled'}
                      </div>
                      {row.short_id ? (
                        <div className="text-xs font-mono text-indigo-600">
                          ID {String(row.short_id).padStart(8, '0')}
                        </div>
                      ) : null}
                      {row.scope_label ? (
                        <div className="text-xs text-gray-500 truncate max-w-[280px]">
                          {row.scope_kind}: {row.scope_label}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 tabular-nums">
                      {row.message_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDateTimeEST(row.last_message_at || row.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 ? (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Page {meta.page} of {meta.totalPages} · {meta.total} conversations
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {selectedUuid ? (
        <ConversationDrawer uuid={selectedUuid} onClose={() => setSelectedUuid(null)} />
      ) : null}
    </div>
  )
}
